import { breakdownFromInclusive, VAT_RATE_BPS } from '../lib/vat';
import type { BusinessProfile } from '../lib/businessProfile';

/**
 * Receipt — SARS-compliant tax invoice render.
 *
 * Phase 1.5b. Two render modes:
 *   1. **On-screen** — preview in the recent-order modal
 *   2. **Print** — print stylesheet (`@media print`) in styles.css
 *      hides everything else; only this component renders to paper
 *
 * Cohesion rule 1: ONE source of truth for receipt layout. Every
 * receipt (cashier reprint, email-receipt PDF generation later,
 * customer-pickup receipt) uses this component.
 *
 * SARS compliance:
 *   - "TAX INVOICE" header
 *   - Supplier legal name + address
 *   - Supplier VAT number (when registered)
 *   - Sequential invoice number
 *   - Date of issue
 *   - Description / qty / unit price / line total
 *   - VAT amount + rate
 *   - Total (VAT-inclusive)
 *
 * For amounts < R5,000 the abridged tax invoice is sufficient
 * (which is what this renders by default — no recipient details).
 * A future "Issue full tax invoice" flow asks for recipient details
 * when the total >= R5,000 or on request.
 */

export interface ReceiptLine {
  name: string;
  variant?: string;            // e.g. "Regular"
  qty: number;
  unitPriceCents: number;      // VAT-inclusive
}

export interface ReceiptData {
  orderNumber: string;              // e.g. "#56998"
  invoiceNumber?: string;           // overrides orderNumber if set
  placedAt: Date;
  customer?: { name?: string; phone?: string; email?: string };
  paymentMethod?: string;           // "Card", "Cash", "EFT", "Account"
  // Optional tip — only shown if tipsEnabled and > 0
  tipCents?: number;
  discountCents?: number;
  discountReason?: string;
  // Card-specific metadata (when paymentMethod is "Card"). Vendor-neutral —
  // populated from whatever external terminal is in use.
  cardApprovalCode?: string;
  cardLast4?: string;
  cardTxnRef?: string;
  lines: ReceiptLine[];
  cashierName?: string;
  stationLabel?: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function fmtDate(d: Date) {
  return d.toLocaleString('en-ZA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Africa/Johannesburg',
  });
}

interface Props {
  business: BusinessProfile;
  receipt: ReceiptData;
}

export default function Receipt({ business, receipt }: Props) {
  const linesGross = receipt.lines.reduce((s, l) => s + l.unitPriceCents * l.qty, 0);
  const discountCents = receipt.discountCents ?? 0;
  const tipCents = receipt.tipCents ?? 0;
  const netBeforeTip = Math.max(0, linesGross - discountCents);
  const totalCents = netBeforeTip + tipCents;
  const { subtotalCents, vatCents } = breakdownFromInclusive(netBeforeTip);

  return (
    <div className="receipt mx-auto bg-white text-black font-mono text-[13px] leading-relaxed max-w-[80mm] p-4 print:p-0 print:max-w-none print:text-[11px]">
      {/* HEADER */}
      <div className="text-center mb-3">
        <p className="text-[11px] font-bold uppercase tracking-widest">Tax Invoice</p>
        <p className="text-[16px] font-bold mt-1">{business.tradingName ?? business.legalName}</p>
        {business.tradingName && (
          <p className="text-[11px]">{business.legalName}</p>
        )}
        <p className="text-[11px] mt-1">{business.addressLine1}</p>
        {business.addressLine2 && <p className="text-[11px]">{business.addressLine2}</p>}
        <p className="text-[11px]">
          {business.city}{business.postalCode ? `, ${business.postalCode}` : ''}, {business.country}
        </p>
        {business.phone && <p className="text-[11px]">{business.phone}</p>}
        {business.email && <p className="text-[11px]">{business.email}</p>}
        {business.vatNumber ? (
          <p className="text-[11px] mt-1">VAT Reg No: <span className="font-bold">{business.vatNumber}</span></p>
        ) : (
          <p className="text-[10px] italic mt-1">(VAT registration pending)</p>
        )}
      </div>

      <div className="border-t border-dashed border-black my-2" />

      {/* META */}
      <div className="mb-2 text-[12px]">
        <div className="flex justify-between">
          <span>Invoice #</span>
          <span className="font-bold tabular-nums">
            {receipt.invoiceNumber ?? `${business.invoiceNumberPrefix ?? ''}${receipt.orderNumber.replace('#', '')}`}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Date</span>
          <span className="tabular-nums">{fmtDate(receipt.placedAt)}</span>
        </div>
        {receipt.cashierName && (
          <div className="flex justify-between">
            <span>Cashier</span>
            <span>{receipt.cashierName}</span>
          </div>
        )}
        {receipt.stationLabel && (
          <div className="flex justify-between">
            <span>Station</span>
            <span>{receipt.stationLabel}</span>
          </div>
        )}
        {receipt.customer?.name && (
          <div className="flex justify-between">
            <span>Customer</span>
            <span>{receipt.customer.name}</span>
          </div>
        )}
      </div>

      <div className="border-t border-dashed border-black my-2" />

      {/* LINES */}
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-dashed border-black">
            <th className="text-left font-bold py-1">Item</th>
            <th className="text-right font-bold py-1 w-12">Qty</th>
            <th className="text-right font-bold py-1 w-20">Total</th>
          </tr>
        </thead>
        <tbody>
          {receipt.lines.map((l, i) => {
            const lineTotal = l.unitPriceCents * l.qty;
            return (
              <tr key={i}>
                <td className="py-1 align-top">
                  {l.name}
                  {l.variant && <span className="block text-[10px] uppercase tracking-wider">{l.variant}</span>}
                  <span className="block text-[10px]">@ {fmtR(l.unitPriceCents)} ea</span>
                </td>
                <td className="text-right py-1 tabular-nums align-top">{l.qty}</td>
                <td className="text-right py-1 tabular-nums font-bold align-top">{fmtR(lineTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />

      {/* TOTALS */}
      <div className="text-[12px] space-y-0.5 mb-2">
        {discountCents > 0 && (
          <div className="flex justify-between">
            <span>Discount{receipt.discountReason ? ` (${receipt.discountReason})` : ''}</span>
            <span className="tabular-nums">−{fmtR(discountCents)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Sub Total (excl. VAT)</span>
          <span className="tabular-nums">{fmtR(subtotalCents)}</span>
        </div>
        <div className="flex justify-between">
          <span>VAT @ {(VAT_RATE_BPS / 100).toFixed(0)}% (incl.)</span>
          <span className="tabular-nums">{fmtR(vatCents)}</span>
        </div>
        {tipCents > 0 && (
          <div className="flex justify-between">
            <span>Tip</span>
            <span className="tabular-nums">{fmtR(tipCents)}</span>
          </div>
        )}
        <div className="border-t border-black mt-1 pt-1 flex justify-between text-[14px] font-bold">
          <span>TOTAL</span>
          <span className="tabular-nums">{fmtR(totalCents)}</span>
        </div>
      </div>

      {receipt.paymentMethod && (
        <>
          <div className="border-t border-dashed border-black my-2" />
          <div className="text-[12px] flex justify-between">
            <span>Paid by</span>
            <span className="font-bold uppercase tracking-wider">{receipt.paymentMethod}</span>
          </div>
          {(receipt.cardApprovalCode || receipt.cardLast4 || receipt.cardTxnRef) && (
            <div className="text-[11px] mt-1 space-y-0.5">
              {receipt.cardLast4 && (
                <div className="flex justify-between">
                  <span>Card</span>
                  <span className="tabular-nums">**** **** **** {receipt.cardLast4}</span>
                </div>
              )}
              {receipt.cardApprovalCode && (
                <div className="flex justify-between">
                  <span>Approval</span>
                  <span className="font-bold tabular-nums">{receipt.cardApprovalCode}</span>
                </div>
              )}
              {receipt.cardTxnRef && (
                <div className="flex justify-between">
                  <span>Txn ref</span>
                  <span className="tabular-nums">{receipt.cardTxnRef}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="border-t border-dashed border-black my-2" />

      {/* FOOTER */}
      <div className="text-center text-[10px] mt-3 space-y-0.5">
        <p>Thank you for visiting</p>
        <p className="font-bold">{business.tradingName ?? business.legalName}</p>
        <p className="mt-2 italic">VAT included at the prevailing rate.</p>
        <p className="mt-3 text-[9px] opacity-70">This receipt was generated electronically.</p>
      </div>
    </div>
  );
}
